import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, ExternalLink, Shield, Building, Users, Monitor, Server, Lock, FileCheck, Database, Network, HardDrive, Workflow } from 'lucide-react';

interface OfficeLocation {
  id: string;
  name: string;
  region: 'Pakistan' | 'USA';
  city: string;
  address: string;
  status: {
    hipaa: {
      compliant: boolean;
      progress: number;
      issues: string[];
    };
    iso27001: {
      compliant: boolean;
      progress: number;
      issues: string[];
    };
  };
  assets: {
    total: number;
    compliant: number;
    nonCompliant: number;
  };
}

const ComplianceStatus = () => {
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<'all' | 'Pakistan' | 'USA'>('all');
  const [expandedDetails, setExpandedDetails] = useState<boolean>(false);
  
  // Office location data with compliance status
  const officeLocations: OfficeLocation[] = [
    // Pakistan Offices
    {
      id: 'prg-ts-01',
      name: 'PRG-TS-01',
      region: 'Pakistan',
      city: 'Lahore',
      address: 'Township, Sector B1',
      status: {
        hipaa: {
          compliant: false,
          progress: 30,
          issues: [
            'Unencrypted database storing PHI data',
            'Weak endpoint security controls on workstations',
            'Missing intrusion detection systems for network monitoring'
          ]
        },
        iso27001: {
          compliant: false,
          progress: 47,
          issues: [
            'Outdated firewalls with missing security updates',
            'Unpatched server vulnerabilities on critical systems',
            'Inadequate network segmentation between critical systems'
          ]
        }
      },
      assets: {
        total: 500,
        compliant: 180,
        nonCompliant: 320
      }
    },
    {
      id: 'prg-ts-03',
      name: 'PRG-TS-03',
      region: 'Pakistan',
      city: 'Lahore',
      address: 'Township, Sector C2',
      status: {
        hipaa: {
          compliant: false,
          progress: 30,
          issues: [
            'Missing endpoint encryption on mobile devices',
            'Default credentials on network equipment',
            'Insecure server configurations exposing PHI data'
          ]
        },
        iso27001: {
          compliant: false,
          progress: 47,
          issues: [
            'Misconfigured access control systems',
            'Unpatched security vulnerabilities in web applications',
            'Insecure remote access configurations'
          ]
        }
      },
      assets: {
        total: 500,
        compliant: 150,
        nonCompliant: 350
      }
    },
    {
      id: 'prg-jt-office',
      name: 'PRG-JT-Office',
      region: 'Pakistan',
      city: 'Lahore',
      address: 'Johar Town, Block F2',
      status: {
        hipaa: {
          compliant: false,
          progress: 30,
          issues: [
            'Insufficient network segmentation for PHI data',
            'Unsecured wireless networks used for clinical systems',
            'Missing data loss prevention controls'
          ]
        },
        iso27001: {
          compliant: false,
          progress: 47,
          issues: [
            'Inadequate malware protection on endpoints',
            'Missing intrusion prevention systems',
            'Unsecured developer access to production systems'
          ]
        }
      },
      assets: {
        total: 500,
        compliant: 142,
        nonCompliant: 358
      }
    },
    {
      id: 'ac-office',
      name: 'AC-Office',
      region: 'Pakistan',
      city: 'Lahore',
      address: 'Askari Complex, Gulberg III',
      status: {
        hipaa: {
          compliant: false,
          progress: 30,
          issues: [
            'Unencrypted backups containing PHI',
            'Inadequate logging of system access to PHI',
            'Missing security controls on medical devices'
          ]
        },
        iso27001: {
          compliant: false,
          progress: 47,
          issues: [
            'Outdated operating systems with known vulnerabilities',
            'Missing security patches on critical infrastructure',
            'Inadequate network monitoring and threat detection'
          ]
        }
      },
      assets: {
        total: 500,
        compliant: 210,
        nonCompliant: 290
      }
    },
    {
      id: 'gt-office',
      name: 'GT-Office',
      region: 'Pakistan',
      city: 'Lahore',
      address: 'Gulberg Town, Main Boulevard',
      status: {
        hipaa: {
          compliant: false,
          progress: 30,
          issues: [
            'Insecure file transfer methods for medical records',
            'Unsecured database servers with PHI',
            'Missing encryption for data in transit'
          ]
        },
        iso27001: {
          compliant: false,
          progress: 47,
          issues: [
            'Misconfigured firewalls allowing unauthorized access',
            'Weak encryption standards for sensitive data',
            'Inadequate backup security controls'
          ]
        }
      },
      assets: {
        total: 500,
        compliant: 95,
        nonCompliant: 405
      }
    },
    {
      id: 'dha-office',
      name: 'DHA-Office',
      region: 'Pakistan',
      city: 'Lahore',
      address: 'DHA Phase 5, Block C',
      status: {
        hipaa: {
          compliant: false,
          progress: 30,
          issues: [
            'Insecure authentication mechanisms on clinical systems',
            'Missing data encryption on medical workstations',
            'Inadequate audit logging of PHI access and usage'
          ]
        },
        iso27001: {
          compliant: false,
          progress: 47,
          issues: [
            'Unpatched server vulnerabilities',
            'Inadequate network segregation for sensitive systems',
            'Misconfigured security appliances'
          ]
        }
      },
      assets: {
        total: 500,
        compliant: 195,
        nonCompliant: 305
      }
    },
    {
      id: 'rwp-office',
      name: 'RWP-Office',
      region: 'Pakistan',
      city: 'Rawalpindi',
      address: 'Satellite Town, Block B',
      status: {
        hipaa: {
          compliant: false,
          progress: 30,
          issues: [
            'Insecure remote access to clinical systems',
            'Unpatched medical devices with known vulnerabilities',
            'Insufficient email security for PHI communications'
          ]
        },
        iso27001: {
          compliant: false,
          progress: 47,
          issues: [
            'Missing endpoint detection and response solutions',
            'Inadequate security monitoring of critical systems',
            'Unpatched virtual infrastructure with known vulnerabilities'
          ]
        }
      },
      assets: {
        total: 500,
        compliant: 165,
        nonCompliant: 335
      }
    },
    {
      id: 'grw-office',
      name: 'GRW-Office',
      region: 'Pakistan',
      city: 'Gujranwala',
      address: 'Civil Lines, Main Boulevard',
      status: {
        hipaa: {
          compliant: false,
          progress: 30,
          issues: [
            'Lack of security for networked medical devices',
            'Default credentials on critical clinical systems',
            'Missing data loss prevention controls'
          ]
        },
        iso27001: {
          compliant: false,
          progress: 47,
          issues: [
            'Outdated antivirus solutions on critical systems',
            'Inadequate network monitoring and alerting',
            'Missing security baselines for server configurations'
          ]
        }
      },
      assets: {
        total: 500,
        compliant: 130,
        nonCompliant: 370
      }
    },
    
    // USA Offices
    {
      id: 'gowners-grove',
      name: 'Gowners Grove Office',
      region: 'USA',
      city: 'Gowners Grove, IL',
      address: '2570 Warrenville Road',
      status: {
        hipaa: {
          compliant: false,
          progress: 40,
          issues: [
            'Incomplete server hardening for systems with ePHI',
            'Missing endpoint encryption on clinical workstations',
            'Inadequate network segmentation for protected health data'
          ]
        },
        iso27001: {
          compliant: false,
          progress: 57,
          issues: [
            'Outdated TLS protocols on web applications',
            'Unpatched critical vulnerabilities on server infrastructure',
            'Inadequate privileged access management controls'
          ]
        }
      },
      assets: {
        total: 500,
        compliant: 240,
        nonCompliant: 260
      }
    },
    {
      id: 'lisle-office',
      name: 'Lisle Office',
      region: 'USA',
      city: 'Lisle, IL',
      address: '4225 Naperville Road',
      status: {
        hipaa: {
          compliant: false,
          progress: 40,
          issues: [
            'Insecure medical device networks',
            'Unencrypted backup media containing PHI',
            'Insufficient security monitoring of clinical systems'
          ]
        },
        iso27001: {
          compliant: false,
          progress: 57,
          issues: [
            'Missing security patches on critical servers',
            'Inadequate network access controls',
            'Insufficient logging and monitoring of security events'
          ]
        }
      },
      assets: {
        total: 500,
        compliant: 255,
        nonCompliant: 245
      }
    }
  ];
  
  // Filter locations by selected region
  const filteredLocations = selectedRegion === 'all' 
    ? officeLocations 
    : officeLocations.filter(location => location.region === selectedRegion);
  
  // Get the selected location details
  const locationDetail = officeLocations.find(location => location.id === selectedLocation);
  
  // Toggle location details
  const toggleLocationDetails = (locationId: string) => {
    if (selectedLocation === locationId) {
      setSelectedLocation(null);
      setExpandedDetails(false);
    } else {
      setSelectedLocation(locationId);
      setExpandedDetails(false);
    }
  };
  
  // Toggle expanded details view
  const toggleExpandedDetails = () => {
    setExpandedDetails(!expandedDetails);
  };
  
  return (
    <div className="container mx-auto px-3 sm:px-6 py-4 sm:py-8">
      {/* HUD-Style Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-10 p-1 border border-red-900/30 rounded-md bg-black/30 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 sm:py-0 sm:gap-4 sm:px-4">
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">SCAN:</span> ACTIVE
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">COMPLIANCE:</span> MONITORING
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">SITES:</span> {officeLocations.length}
          </div>
        </div>
        <div className="flex items-center px-2 py-2 sm:py-0">
          <div className="h-2 bg-gradient-to-r from-red-900 via-red-600 to-red-900 animate-pulse mx-2 sm:mx-4" style={{ width: '60px', maxWidth: '25vw' }}></div>
          <div className="text-red-400 font-mono text-xs px-2 sm:px-4">
            <span className="text-slate-500">TIME:</span> {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className="mb-8">
        <div className="relative">
          <h1 className="text-2xl sm:text-4xl font-bold mb-1">
            Compliance <span className="text-red-500">Status</span>
          </h1>
          <div className="absolute -top-2 -right-2 w-2 h-2 bg-red-600 rounded-full animate-ping"></div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-red-400 text-xs sm:text-base mb-4">
          <Shield className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>HIPAA & ISO 27001</span>
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 hidden sm:block"></span>
          <span className="hidden sm:inline">Office Compliance Dashboard</span>
        </div>
        <p className="text-slate-300 text-xs sm:text-sm border-l-4 border-red-900/50 pl-3 pr-4 py-2 bg-black/30 rounded-r-md mb-6">
          This dashboard provides real-time compliance status for all office locations against HIPAA and ISO 27001 
          standards. Each location is evaluated based on the compliance of IT assets and infrastructure. 
          Select any location to view detailed information about compliance issues.
        </p>
      </div>

      {/* Filter Controls */}
      <div className="mb-6 flex flex-wrap gap-3">
        <button 
          onClick={() => setSelectedRegion('all')}
          className={`px-4 py-2 rounded-lg border transition-all ${
            selectedRegion === 'all' 
            ? 'bg-red-900/20 border-red-900/40 text-white' 
            : 'bg-slate-900/50 border-slate-800/60 text-slate-400 hover:bg-red-900/10 hover:border-red-900/30'
          }`}
        >
          <Building className="h-4 w-4 inline-block mr-1.5" />
          All Locations
        </button>
        <button 
          onClick={() => setSelectedRegion('Pakistan')}
          className={`px-4 py-2 rounded-lg border transition-all ${
            selectedRegion === 'Pakistan' 
            ? 'bg-red-900/20 border-red-900/40 text-white' 
            : 'bg-slate-900/50 border-slate-800/60 text-slate-400 hover:bg-red-900/10 hover:border-red-900/30'
          }`}
        >
          <Building className="h-4 w-4 inline-block mr-1.5" />
          Pakistan Offices
        </button>
        <button 
          onClick={() => setSelectedRegion('USA')}
          className={`px-4 py-2 rounded-lg border transition-all ${
            selectedRegion === 'USA' 
            ? 'bg-red-900/20 border-red-900/40 text-white' 
            : 'bg-slate-900/50 border-slate-800/60 text-slate-400 hover:bg-red-900/10 hover:border-red-900/30'
          }`}
        >
          <Building className="h-4 w-4 inline-block mr-1.5" />
          USA Offices
        </button>
      </div>

      {/* Selected Location Details */}
      {selectedLocation && locationDetail && (
        <div className="mb-8 bg-slate-950/70 border border-slate-800 rounded-lg overflow-hidden transition-all">
          <div className="border-b border-slate-800">
            <div className="flex justify-between items-center p-4">
              <div className="flex items-center">
                <div className="p-2 bg-red-900/30 rounded-lg border border-red-900/40 mr-3">
                  <Building className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{locationDetail.name}</h3>
                  <p className="text-slate-400 text-sm">{locationDetail.city}, {locationDetail.region}</p>
                </div>
              </div>
              <button 
                onClick={toggleExpandedDetails}
                className="p-2 bg-black/40 rounded-lg border border-slate-800 hover:border-red-900/30 transition-colors"
              >
                {expandedDetails ? 
                  <ChevronDown className="h-5 w-5 text-slate-400" /> : 
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                }
              </button>
            </div>
          </div>
          
          <div className="p-4 border-b border-slate-800">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* HIPAA Compliance */}
              <div className="bg-black/40 p-4 rounded-lg border border-slate-800">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold flex items-center">
                    <Shield className="h-4 w-4 mr-2 text-red-500" />
                    HIPAA Compliance
                  </h4>
                  <div className={`px-2 py-1 rounded-full text-xs ${
                    locationDetail.status.hipaa.compliant 
                      ? 'bg-green-900/20 text-green-400 border border-green-900/30' 
                      : 'bg-red-900/20 text-red-400 border border-red-900/30'
                  }`}>
                    {locationDetail.status.hipaa.compliant ? 'Compliant' : 'Non-Compliant'}
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-slate-400">Compliance Progress</span>
                    <span className="text-xs font-medium">{locationDetail.status.hipaa.progress}%</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        locationDetail.status.hipaa.progress >= 80 
                          ? 'bg-green-500' 
                          : locationDetail.status.hipaa.progress >= 50 
                            ? 'bg-yellow-500' 
                            : 'bg-red-500'
                      }`}
                      style={{ width: `${locationDetail.status.hipaa.progress}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="text-sm text-slate-400">
                  <p className="mb-2">
                    <AlertTriangle className="h-4 w-4 text-red-500 inline-block mr-1" />
                    As per AlertStrike scan results, this branch is non-compliant with HIPAA regulations.
                  </p>
                  {expandedDetails && (
                    <div className="mt-3 pl-3 border-l-2 border-red-900/30">
                      <p className="font-medium text-slate-300 mb-1">Key Issues:</p>
                      <ul className="list-disc pl-4 space-y-1">
                        {locationDetail.status.hipaa.issues.map((issue, index) => (
                          <li key={index}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              
              {/* ISO 27001 Compliance */}
              <div className="bg-black/40 p-4 rounded-lg border border-slate-800">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold flex items-center">
                    <FileCheck className="h-4 w-4 mr-2 text-red-500" />
                    ISO 27001 Compliance
                  </h4>
                  <div className={`px-2 py-1 rounded-full text-xs ${
                    locationDetail.status.iso27001.compliant 
                      ? 'bg-green-900/20 text-green-400 border border-green-900/30' 
                      : 'bg-red-900/20 text-red-400 border border-red-900/30'
                  }`}>
                    {locationDetail.status.iso27001.compliant ? 'Compliant' : 'Non-Compliant'}
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-slate-400">Compliance Progress</span>
                    <span className="text-xs font-medium">{locationDetail.status.iso27001.progress}%</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        locationDetail.status.iso27001.progress >= 80 
                          ? 'bg-green-500' 
                          : locationDetail.status.iso27001.progress >= 50 
                            ? 'bg-yellow-500' 
                            : 'bg-red-500'
                      }`}
                      style={{ width: `${locationDetail.status.iso27001.progress}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="text-sm text-slate-400">
                  <p className="mb-2">
                    <AlertTriangle className="h-4 w-4 text-red-500 inline-block mr-1" />
                    As per AlertStrike scan results, this branch is non-compliant with ISO 27001 standards.
                  </p>
                  {expandedDetails && (
                    <div className="mt-3 pl-3 border-l-2 border-red-900/30">
                      <p className="font-medium text-slate-300 mb-1">Key Issues:</p>
                      <ul className="list-disc pl-4 space-y-1">
                        {locationDetail.status.iso27001.issues.map((issue, index) => (
                          <li key={index}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Asset Compliance Details */}
          <div className="p-4">
            <h4 className="font-bold mb-3 flex items-center">
              <Monitor className="h-4 w-4 mr-2 text-red-500" />
              IT Asset Compliance Status
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-black/40 p-3 rounded-lg border border-slate-800 flex flex-col">
                <span className="text-xs text-slate-400 mb-1">Total Endpoints</span>
                <span className="text-xl font-bold">{locationDetail.assets.total}</span>
              </div>
              
              <div className="bg-black/40 p-3 rounded-lg border border-slate-800 flex flex-col">
                <span className="text-xs text-slate-400 mb-1">Compliant Assets</span>
                <span className="text-xl font-bold text-green-400">{locationDetail.assets.compliant}</span>
              </div>
              
              <div className="bg-black/40 p-3 rounded-lg border border-slate-800 flex flex-col">
                <span className="text-xs text-slate-400 mb-1">Non-Compliant Assets</span>
                <span className="text-xl font-bold text-red-400">{locationDetail.assets.nonCompliant}</span>
              </div>
            </div>
            
            <div className="mb-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-slate-400">Asset Compliance Rate</span>
                <span className="text-xs font-medium">
                  {Math.round((locationDetail.assets.compliant / locationDetail.assets.total) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-red-800 to-red-600 rounded-full"
                  style={{ width: `${(locationDetail.assets.compliant / locationDetail.assets.total) * 100}%` }}
                ></div>
              </div>
            </div>
            
            {expandedDetails && (
              <div className="bg-black/40 p-3 rounded-lg border border-slate-800 mt-4">
                <h5 className="font-medium mb-2 text-sm">Common Non-Compliance Issues:</h5>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li className="flex items-start">
                    <AlertTriangle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Outdated operating systems and software without security patches</span>
                  </li>
                  <li className="flex items-start">
                    <AlertTriangle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Missing endpoint encryption for data at rest</span>
                  </li>
                  <li className="flex items-start">
                    <AlertTriangle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Insufficient access controls and authentication mechanisms</span>
                  </li>
                  <li className="flex items-start">
                    <AlertTriangle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Incomplete audit logging and monitoring capabilities</span>
                  </li>
                  <li className="flex items-start">
                    <AlertTriangle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Unpatched vulnerabilities in network infrastructure</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Office Locations Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredLocations.map(location => (
          <div 
            key={location.id}
            className={`bg-slate-950/70 border ${selectedLocation === location.id ? 'border-red-900/50' : 'border-slate-800'} rounded-lg overflow-hidden transition-all hover:border-red-900/30 cursor-pointer`}
            onClick={() => toggleLocationDetails(location.id)}
          >
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="p-2 bg-black/60 rounded-lg border border-slate-800 mr-3">
                    <Building className="h-4 w-4 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-bold">{location.name}</h3>
                    <p className="text-sm text-slate-400">{location.city}, {location.region}</p>
                  </div>
                </div>
                
                <div className="bg-red-900/20 border border-red-900/30 text-red-400 rounded-full px-2 py-0.5 text-xs">
                  Non-Compliant
                </div>
              </div>
              
              {/* HIPAA Status */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-400 flex items-center">
                    <Shield className="h-3 w-3 mr-1 text-red-500" />
                    HIPAA
                  </span>
                  <span className="text-xs font-medium">{location.status.hipaa.progress}%</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      location.status.hipaa.progress >= 80 
                        ? 'bg-green-500' 
                        : location.status.hipaa.progress >= 50 
                          ? 'bg-yellow-500' 
                          : 'bg-red-500'
                    }`}
                    style={{ width: `${location.status.hipaa.progress}%` }}
                  ></div>
                </div>
              </div>
              
              {/* ISO 27001 Status */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-400 flex items-center">
                    <FileCheck className="h-3 w-3 mr-1 text-red-500" />
                    ISO 27001
                  </span>
                  <span className="text-xs font-medium">{location.status.iso27001.progress}%</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      location.status.iso27001.progress >= 80 
                        ? 'bg-green-500' 
                        : location.status.iso27001.progress >= 50 
                          ? 'bg-yellow-500' 
                          : 'bg-red-500'
                    }`}
                    style={{ width: `${location.status.iso27001.progress}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Asset Status */}
              <div className="flex items-center text-xs text-slate-400 mt-4">
                <Users className="h-3 w-3 mr-1" />
                <span>500 Endpoints</span>
                <span className="mx-2">•</span>
                <span className="text-red-400">{location.assets.nonCompliant} Non-Compliant</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Compliance Legend */}
      <div className="mt-8 bg-black/30 border border-slate-800 rounded-lg p-4">
        <h3 className="font-bold mb-3 flex items-center">
          <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-4 w-4 mr-2" />
          IT & System Security Compliance Legend
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center">
              <Shield className="h-4 w-4 mr-2 text-red-500" />
              HIPAA Technical Security Controls
            </h4>
            <ul className="text-xs text-slate-400 space-y-1 pl-6">
              <li className="list-disc flex items-center">
                <Server className="h-3 w-3 mr-1 text-red-500" />
                <span>Server & Infrastructure Security</span>
              </li>
              <li className="list-disc flex items-center">
                <Network className="h-3 w-3 mr-1 text-red-500" />
                <span>Network Security & Segmentation</span>
              </li>
              <li className="list-disc flex items-center">
                <Lock className="h-3 w-3 mr-1 text-red-500" />
                <span>Access Control & Authentication</span>
              </li>
              <li className="list-disc flex items-center">
                <HardDrive className="h-3 w-3 mr-1 text-red-500" />
                <span>Data Encryption & Protection</span>
              </li>
              <li className="list-disc flex items-center">
                <Workflow className="h-3 w-3 mr-1 text-red-500" />
                <span>Audit Controls & Monitoring</span>
              </li>
              <li className="list-disc flex items-center">
                <Monitor className="h-3 w-3 mr-1 text-red-500" />
                <span>Endpoint & Device Security</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center">
              <FileCheck className="h-4 w-4 mr-2 text-red-500" />
              ISO 27001 Technical Controls
            </h4>
            <ul className="text-xs text-slate-400 space-y-1 pl-6">
              <li className="list-disc flex items-center">
                <Network className="h-3 w-3 mr-1 text-red-500" />
                <span>Network & Communications Security</span>
              </li>
              <li className="list-disc flex items-center">
                <Server className="h-3 w-3 mr-1 text-red-500" />
                <span>System Acquisition & Development</span>
              </li>
              <li className="list-disc flex items-center">
                <Database className="h-3 w-3 mr-1 text-red-500" />
                <span>Cryptography & Data Security</span>
              </li>
              <li className="list-disc flex items-center">
                <Lock className="h-3 w-3 mr-1 text-red-500" />
                <span>Access Management & Authentication</span>
              </li>
              <li className="list-disc flex items-center">
                <Shield className="h-3 w-3 mr-1 text-red-500" />
                <span>Operations Security</span>
              </li>
              <li className="list-disc flex items-center">
                <Monitor className="h-3 w-3 mr-1 text-red-500" />
                <span>Endpoint Security & Protection</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-slate-800">
          <h4 className="text-sm font-medium mb-2 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-2 text-red-500" />
            Common Technical Security Issues
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ul className="text-xs text-slate-400 space-y-1 pl-6">
              <li className="list-disc">Unpatched servers and workstations</li>
              <li className="list-disc">Missing data encryption standards</li>
              <li className="list-disc">Weak authentication controls</li>
              <li className="list-disc">Inadequate network segregation</li>
              <li className="list-disc">Insufficient security monitoring</li>
            </ul>
            <ul className="text-xs text-slate-400 space-y-1 pl-6">
              <li className="list-disc">Insecure remote access methods</li>
              <li className="list-disc">Vulnerable web applications</li>
              <li className="list-disc">Outdated security appliances</li> 
              <li className="list-disc">Misconfigured firewalls and systems</li>
              <li className="list-disc">Inadequate backup security</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceStatus;